import clsx from "clsx";

export interface UsageBarProps {
  label: string;
  used: number;
  /** null = unlimited; 0 = forbidden; integer = cap. */
  limit: number | null;
  /** Hide the numeric value (compact sidebar mode). */
  compact?: boolean;
  /** Optional icon node rendered to the left of the label. */
  icon?: React.ReactNode;
  /** Render for a dark (rail) surface instead of the default light card. */
  dark?: boolean;
}

function pct(used: number, limit: number | null): number {
  if (limit == null) return 0;
  if (limit <= 0) return 100;
  return Math.min(100, Math.round((used / limit) * 100));
}

function toneFor(
  used: number,
  limit: number | null,
): "ok" | "warn" | "danger" | "unlimited" | "blocked" {
  if (limit == null) return "unlimited";
  if (limit <= 0) return "blocked";
  const p = pct(used, limit);
  if (p >= 90) return "danger";
  if (p >= 60) return "warn";
  return "ok";
}

const BAR_TONE: Record<string, string> = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  danger: "bg-red-500",
  unlimited: "bg-indigo-500",
  blocked: "bg-gray-400",
};

const VALUE_TONE: Record<string, string> = {
  ok: "text-gray-700",
  warn: "text-amber-700",
  danger: "text-red-700",
  unlimited: "text-indigo-700",
  blocked: "text-gray-500",
};

const VALUE_TONE_DARK: Record<string, string> = {
  ok: "text-rail-foreground",
  warn: "text-amber-400",
  danger: "text-red-400",
  unlimited: "text-indigo-300",
  blocked: "text-rail-muted",
};

export function formatLimitValue(used: number, limit: number | null): string {
  if (limit == null) return `${used.toLocaleString()} / Unlimited`;
  if (limit <= 0) return "Not included";
  return `${used.toLocaleString()} / ${limit.toLocaleString()}`;
}

const UsageBar = ({
  label,
  used,
  limit,
  compact = false,
  icon,
  dark = false,
}: UsageBarProps) => {
  const tone = toneFor(used, limit);
  const width = tone === "unlimited" ? 12 : pct(used, limit);

  return (
    <div className="w-full">
      <div
        className={clsx(
          "flex items-center justify-between gap-2",
          compact ? "mb-1" : "mb-1.5",
        )}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          {icon ? (
            <span
              className={clsx(
                "shrink-0",
                dark ? "text-rail-muted" : "text-gray-500",
              )}
            >
              {icon}
            </span>
          ) : null}
          <span
            className={clsx(
              "truncate font-medium",
              compact ? "text-[11px]" : "text-xs",
              dark ? "text-rail-foreground/85" : "text-gray-700",
            )}
          >
            {label}
          </span>
        </div>
        <span
          className={clsx(
            "tabular-nums whitespace-nowrap font-medium",
            compact ? "text-[11px]" : "text-xs",
            (dark ? VALUE_TONE_DARK : VALUE_TONE)[tone],
          )}
        >
          {formatLimitValue(used, limit)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={tone === "unlimited" ? 0 : used}
        aria-valuemin={0}
        aria-valuemax={limit ?? undefined}
        className={clsx(
          "w-full overflow-hidden rounded-full",
          dark ? "bg-rail-foreground/10" : "bg-gray-100",
          compact ? "h-1" : "h-1.5",
        )}
      >
        <div
          className={clsx(
            "h-full rounded-full transition-[width] duration-200 ease-out",
            BAR_TONE[tone],
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
};

export default UsageBar;
